import React, { useState, useEffect } from 'react';
import { Typography, Spin, Button, Space } from 'antd';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { ProjectCard } from './project-card';
import { useListAvatarProjects } from '../../hooks/use-list-avatar-projects';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';
import { C } from '../../constants/design-tokens';
import { AvatarProjectItem } from '../../../../types/avatar-video';
import { motion } from 'framer-motion';
import Masonry from 'react-responsive-masonry';

const { Text } = Typography;

const DISPLAY_COUNT = 12;

interface ProjectHistoryProps {
  onEdit: (projectId: string) => void;
  onRegenerate: (projectId: string) => void;
  columnsCount?: number;
}

export const ProjectHistory: React.FC<ProjectHistoryProps> = ({ onEdit, onRegenerate, columnsCount }) => {
  const { fontSize, spacing, screens } = useResponsiveSizes();
  const { listAvatarProjects } = useListAvatarProjects();

  const [projects, setProjects] = useState<AvatarProjectItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(DISPLAY_COUNT);

  const loadProjects = async () => {
    setIsLoading(true);
    const res = await listAvatarProjects();
    if (res) {
      const timeOf = (p: AvatarProjectItem): number => new Date(p.updatedAt ?? p.createdAt).getTime();
      const sorted = [...res.items].sort((a, b) => timeOf(b) - timeOf(a));
      setProjects(sorted);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadProjects();
  }, [listAvatarProjects]);

  const handleEdit = (projectId: string) => {
    onEdit(projectId);
  };

  const handleRegenerate = (projectId: string) => {
    onRegenerate(projectId);
  };

  const visibleProjects = projects.slice(0, displayCount);
  const hasMore = displayCount < projects.length;

  if (isLoading) {
    return (
      <div
        style={{
          textAlign: 'center',
          margin: screens.xs ? '100px 0' : '150px 0',
          color: C.textMuted,
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Spin indicator={<LoadingOutlined spin />} size='large' />
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div
        style={{
          textAlign: 'center',
          margin: screens.xs ? '50px 0' : '100px 0',
          color: C.textMuted,
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Text style={{ fontSize: fontSize.xlg }}>No hay proyectos generados</Text>
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadProjects}
            size='small'
            title='Actualizar'
            style={{ backgroundColor: C.bg, color: C.textPrimary, position: 'relative' }}
          />
        </Space>
        <Text style={{ fontSize: fontSize.base, color: C.textSecondary }}>
          {visibleProjects.length} de {projects.length} proyectos
        </Text>
      </Space>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Masonry columnsCount={columnsCount} gutter={screens.xs ? '12px' : '16px'}>
          {visibleProjects.map((project, index) => (
            <motion.div
              key={project.projectId}
              style={{ width: '100%' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: index * 0.03,
                ease: [0.25, 0.1, 0.25, 1]
              }}
            >
              <ProjectCard
                projectId={project.projectId}
                name={project.name}
                status={project.status}
                totalScenes={project.totalScenes}
                completedScenes={project.completedScenes}
                failedScenes={project.failedScenes}
                createdAt={project.createdAt}
                updatedAt={project.updatedAt}
                hasPayload={!!project.payload}
                onEdit={handleEdit}
                onRegenerate={handleRegenerate}
              />
            </motion.div>
          ))}
        </Masonry>
      </div>

      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: spacing.lg }}>
          <Button onClick={() => setDisplayCount(prev => prev + DISPLAY_COUNT)}>
            Cargar más · {projects.length - displayCount} restantes
          </Button>
        </div>
      )}
    </div>
  );
};
